import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { CaseStudy } from '../types';

const CaseStudiesSection = () => {
  const [activeCase, setActiveCase] = useState(0);

  const caseStudies: CaseStudy[] = [
  {
    id: "1",
    industry: "E-commerce",
    companyName: "StyleHub Fashion",
    companyLogo: "https://images.unsplash.com/photo-1677508261025-b68bbaf78796",
    companyLogoAlt: "StyleHub Fashion logo showing modern clothing store branding",
    challenge: "Manual customer support was overwhelming during peak seasons, with 500+ daily inquiries and 4-hour average response times leading to abandoned carts",
    solution: "Implemented WhatsApp Business API automation with AI-powered responses for order tracking, size recommendations, and return policies. Integrated with Shopify for real-time inventory updates",
    results: [
    {
      metric: "Response Time",
      value: "4 hours → 30 seconds",
      description: "99% faster customer support"
    },
    {
      metric: "Cart Recovery",
      value: "+47%",
      description: "Automated abandoned cart messages"
    },
    {
      metric: "Time Saved",
      value: "65 hours/month",
      description: "Support team capacity freed"
    }],

    testimonial: {
      quote: "WhatsApp automation transformed our customer experience. We're now responding instantly to hundreds of customers while our team focuses on complex issues. The ROI was evident within the first month.",
      author: "Sarah Martinez",
      position: "Operations Director",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb208ce5-1763292286266.png",
      avatarAlt: "Sarah Martinez, Operations Director at StyleHub Fashion, professional woman with dark hair in business attire"
    },
    beforeAfter: {
      before: "Manual responses to 500+ daily messages, 4-hour delays, 23% cart abandonment",
      after: "Automated instant responses, 30-second average reply time, 12% cart abandonment"
    }
  },
  {
    id: "2",
    industry: "Real Estate",
    companyName: "Prime Properties Group",
    companyLogo: "https://images.unsplash.com/photo-1656646425179-13b84e0aec2c",
    companyLogoAlt: "Prime Properties Group logo featuring modern real estate building design",
    challenge: "Agents spent 3-4 hours daily responding to property inquiries, scheduling viewings, and sending follow-ups, missing hot leads during off-hours",
    solution: "Created email and WhatsApp automation workflows for instant property details, automated viewing scheduling via Calendly integration, and personalized follow-up sequences based on property interest",
    results: [
    {
      metric: "Lead Response",
      value: "24/7 instant",
      description: "Never miss a hot lead"
    },
    {
      metric: "Viewing Bookings",
      value: "+83%",
      description: "Automated scheduling system"
    },
    {
      metric: "Agent Productivity",
      value: "52 hours/month",
      description: "Time saved per agent"
    }],

    testimonial: {
      quote: "The automation handles all initial inquiries and scheduling, so our agents only engage with qualified, interested buyers. We've closed 30% more deals with the same team size.",
      author: "Michael Chen",
      position: "Managing Broker",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14a5ca983-1763300171126.png",
      avatarAlt: "Michael Chen, Managing Broker at Prime Properties Group, professional man in suit with confident smile"
    },
    beforeAfter: {
      before: "Manual inquiry responses, missed after-hours leads, 3-4 hours daily admin work",
      after: "24/7 automated responses, instant property details, automated viewing scheduling"
    }
  }];


  const currentCase = caseStudies[activeCase];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Real Results from Real Businesses
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            See how communication automation drives measurable business impact
          </p>
        </div>

        <div className="flex justify-center gap-4 mb-8">
          {caseStudies.map((study, index) =>
          <button
            key={study.id}
            onClick={() => setActiveCase(index)}
            className={`px-6 py-3 rounded-lg font-medium transition-colors duration-fast ${
            activeCase === index ?
            'bg-primary text-primary-foreground' :
            'bg-surface text-text-secondary hover:bg-card border border-border'}`
            }>

              {study.industry}
            </button>
          )}
        </div>

        <div className="max-w-6xl mx-auto bg-card border border-border rounded-xl overflow-hidden shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Image
                  src={currentCase.companyLogo}
                  alt={currentCase.companyLogoAlt}
                  className="w-16 h-16 rounded-lg object-cover" />

                <div>
                  <h3 className="text-2xl font-bold text-text-primary">
                    {currentCase.companyName}
                  </h3>
                  <p className="text-text-secondary">{currentCase.industry}</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-text-primary mb-2 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-error" />
                    Challenge
                  </h4>
                  <p className="text-text-secondary">{currentCase.challenge}</p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-text-primary mb-2 flex items-center gap-2">
                    <Icon name="Lightbulb" size={20} className="text-accent" />
                    Solution
                  </h4>
                  <p className="text-text-secondary">{currentCase.solution}</p>
                </div>

                <div className="bg-surface rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-text-primary mb-4">
                    Before & After
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Icon name="X" size={16} className="text-error mt-1 flex-shrink-0" />
                      <p className="text-sm text-text-secondary">{currentCase.beforeAfter.before}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                      <p className="text-sm text-text-secondary">{currentCase.beforeAfter.after}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} className="text-success" />
                Results & Impact
              </h4>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {currentCase.results.map((result, index) =>
                <div key={index} className="bg-surface border border-border rounded-lg p-6">
                    <div className="text-sm text-text-secondary mb-1">{result.metric}</div>
                    <div className="text-3xl font-bold text-primary mb-2">{result.value}</div>
                    <div className="text-sm text-text-secondary">{result.description}</div>
                  </div>
                )}
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <div className="flex items-start gap-4 mb-4">
                  <Image
                    src={currentCase.testimonial.avatar}
                    alt={currentCase.testimonial.avatarAlt}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0" />

                  <div>
                    <div className="font-semibold text-text-primary">
                      {currentCase.testimonial.author}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {currentCase.testimonial.position}
                    </div>
                  </div>
                </div>
                <p className="text-text-secondary italic">"{currentCase.testimonial.quote}"</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <Button variant="default" size="lg" iconName="FileText" iconPosition="left">
            View All Case Studies
          </Button>
        </div>
      </div>
    </section>);

};

export default CaseStudiesSection;